import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { NgIf, NgFor } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { LegalDocument, LegalDocumentFile } from '../../../models/legal-document';
import {
	LegalDocumentService, LEGAL_DOCUMENT_FIELD, GENERAL_SALES_CONDITIONS, SALES_FRAMEWORK, TERM_OF_USE
} from '../../../services/legal-document.service';
import { pageTransitions } from '../../../animations/page-transitions';
import { User } from '../../../models/user';
import { UserService } from '../../../services/user.service';
import { TranslationService } from '../../../services/translation.service';
import { OrderByDirection, OrderBy } from '../../../utils/constant';
import { LANGUAGE_CODE_OPTIONS } from '../../../models/list';
import { SafeUrlPipe } from '../../../pipes/safeurl.pipe';
import { LoadingSpinnerComponent } from '../../utils/loading-spinner.component';

@Component({
	selector: 'copo-legal-documents',
	templateUrl: './legal-documents.component.html',
	styleUrl: './legal-documents.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [TranslateModule, NgIf, LoadingSpinnerComponent, NgFor, MatRippleModule, SafeUrlPipe]
})
export class LegalDocumentComponent implements OnDestroy {
	@HostBinding('class.passthrough') componentClass = true;

	user: User;
	title!: string;

	legalDocumentsGSC: LegalDocument[] = [];
	legalDocumentGCSSub: Subscription;
	isLoadingGsc = true;

	legalDocumentsSF: LegalDocument[] = [];
	legalDocumentSFSub: Subscription;
	isLoadingSf = true;

	legalDocumentsTOU: LegalDocument[] = [];
	legalDocumentTOUSub: Subscription;
	isLoadingTou = true;

	readonly languageOptions = LANGUAGE_CODE_OPTIONS;

	constructor(
		private readonly userService: UserService,
		private readonly legalDocumentService: LegalDocumentService,
		private readonly translationService: TranslationService
	) {
		this.user = this.userService.user;

		// get legal documents - general sales condition
		this.legalDocumentGCSSub = this.legalDocumentService.getLegalDocument(
			LEGAL_DOCUMENT_FIELD,
			this.legalDocumentService.createLuceneFilter(GENERAL_SALES_CONDITIONS),
			1,
			1000,
			new OrderBy('areaRef', OrderByDirection.ASC),
			false
		)
			.subscribe({
				next: res => {
					this.legalDocumentsGSC = this.getSortedLegalDocumentList(res.results);
					for (const gsc of this.legalDocumentsGSC) {
						gsc.files = this.getSortedLegalDocumentFileList(gsc.files);
					}
				},
				complete: () => {
					this.isLoadingGsc = false;
				}
			});

		// get legal documents - sales framework
		this.legalDocumentSFSub = this.legalDocumentService
			.getLegalDocument(
				LEGAL_DOCUMENT_FIELD,
				this.legalDocumentService.createLuceneFilter(SALES_FRAMEWORK),
				1,
				1000,
				new OrderBy('areaRef', OrderByDirection.ASC),
				false
			)
			.subscribe({
				next: res => {
					this.legalDocumentsSF = this.getSortedLegalDocumentList(res.results);
					for (const sf of this.legalDocumentsSF) {
						sf.files = this.getSortedLegalDocumentFileList(sf.files);
					}
				},
				complete: () => {
					this.isLoadingSf = false;
				}
			});

		// get legal documents - terms of use
		this.legalDocumentTOUSub = this.legalDocumentService
			.getLegalDocument(
				LEGAL_DOCUMENT_FIELD,
				this.legalDocumentService.createLuceneFilter(TERM_OF_USE),
				1,
				1000,
				new OrderBy('areaRef', OrderByDirection.ASC),
				false
			)
			.subscribe({
				next: res => {
					this.legalDocumentsTOU = this.getSortedLegalDocumentList(res.results);
					for (const tou of this.legalDocumentsTOU) {
						tou.files = this.getSortedLegalDocumentFileList(tou.files);
					}
				},
				complete: () => {
					this.isLoadingTou = false;
				}
			});
	}

	getDocumentTitle(files: LegalDocumentFile[]) {
		const userFile = files.find(value =>
			value.lang === this.translationService.getLangWithRegion()) ?? files.find(value =>
			value.lang === 'en-GB');
		return userFile?.title ?? '';
	}

	getURL(file: LegalDocumentFile, id: number) {
		return this.legalDocumentService.getFileURL(file, id);
	}

	ngOnDestroy() {
		this.legalDocumentGCSSub.unsubscribe();
		this.legalDocumentSFSub.unsubscribe();
		this.legalDocumentTOUSub.unsubscribe();
	}

	getLang(code: keyof typeof LANGUAGE_CODE_OPTIONS) {
		return this.languageOptions[code];
	}

	getFileURL(file: LegalDocumentFile, id: number) {
		return this.legalDocumentService.getFileURL(file, id);
	}

	getSortedLegalDocumentList(list: LegalDocument[]) {
		return list.sort((a, b) => this.getDocumentTitle(a.files).localeCompare(this.getDocumentTitle(b.files)));
	}

	getSortedLegalDocumentFileList(list: LegalDocumentFile[]) {
		list.sort((a, b) => this.getLang(a.lang).localeCompare(this.getLang(b.lang)));
		const englishFile = list.find(el => el.lang === 'en-GB');
		if (englishFile) {
			const foundIdx = list.findIndex(el => el.lang === 'en-GB');
			list.splice(foundIdx, 1);
			list.unshift(englishFile);
		}
		return list;
	}
}
